import { Bell, FileText, Menu, Search, Store, User, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from '../../assets/scss/layout/AdminHeader.module.scss';
import api from '../../config/axios-conf';
import { PERMISSIONS } from '../../config/permissions';
import { useAdminHeaderSlot } from '../../contexts/AdminHeaderContext';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuthStore } from '../../stores/authStore';
import { formatCurrency, formatPhoneNumber } from '../../utils/formatters';

const ROLE_LABELS = {
    admin: 'QUẢN TRỊ VIÊN',
    manage: 'QUẢN LÝ',
    employee: 'NHÂN VIÊN',
};

export default function AdminHeader({ onToggleSidebar, shopName }) {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useAuthStore((state) => state.user);
    const { hasPermission } = usePermissions();
    const name = user?.name || user?.userName || 'Admin';
    const role = user?.role || 'admin';
    const initials = name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    const { slot } = useAdminHeaderSlot();

    const isBillsPage = location.pathname === '/admin/bills';
    const isCustomersPage = location.pathname === '/admin/customers';
    const showLeftSearch = isBillsPage || isCustomersPage;
    const searchPlaceholder = isCustomersPage
        ? 'Tìm kiếm khách hàng: tên, số điện thoại...'
        : 'Tìm kiếm hoá đơn, khách hàng...';
    const searchBasePath = isCustomersPage ? '/admin/customers' : '/admin/bills';

    const [searchText, setSearchText] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const debouncedSearch = useDebounce(searchText, 300);
    const searchRef = useRef(null);

    // Sync: khi URL không còn ?search= → clear input
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (!params.get('search')) {
            setSearchText('');
            setSuggestions([]);
            setShowSuggestions(false);
        }
    }, [location.search]);

    // Fetch gợi ý khi gõ (chỉ áp dụng cho trang hoá đơn)
    useEffect(() => {
        if (!isBillsPage || !debouncedSearch.trim() || debouncedSearch.trim().length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const fetchSuggestions = async () => {
            setSuggestionsLoading(true);
            try {
                const { data } = await api.get('/bills/search', {
                    params: { keyword: debouncedSearch.trim(), limit: 5, page: 1 },
                });
                const bills = data.bills || data.data || [];
                setSuggestions(bills);
                setShowSuggestions(true);
            } catch {
                setSuggestions([]);
            } finally {
                setSuggestionsLoading(false);
            }
        };

        fetchSuggestions();
    }, [debouncedSearch, isBillsPage]);

    // Đóng dropdown khi click ra ngoài
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearch = (e) => {
        if (e.key === 'Enter' && searchText.trim()) {
            setShowSuggestions(false);
            setSuggestions([]);
            navigate(`${searchBasePath}?search=${encodeURIComponent(searchText.trim())}`);
        }
    };

    const handleSearchSubmit = () => {
        if (searchText.trim()) {
            setShowSuggestions(false);
            setSuggestions([]);
            navigate(`${searchBasePath}?search=${encodeURIComponent(searchText.trim())}`);
        }
    };

    const handleSelectBill = (billId) => {
        setShowSuggestions(false);
        setSearchText('');
        navigate(`/admin/bills/${billId}`);
    };

    const handleClear = () => {
        setSearchText('');
        setSuggestions([]);
        setShowSuggestions(false);
        if (showLeftSearch && location.search.includes('search=')) {
            navigate(searchBasePath);
        }
    };

    return (
        <header className={styles.adminTopbar}>
            <div className={styles.adminTopbarLeft}>
                <button className={styles.adminTopbarHamburger} onClick={onToggleSidebar}>
                    <Menu />
                </button>
                {showLeftSearch && (
                    <div className={styles.adminTopbarSearch} ref={searchRef}>
                        <div className={styles.adminTopbarSearchWrap}>
                            <span className={styles.adminTopbarSearchIcon}>
                                <Search />
                            </span>
                            <input
                                type="text"
                                className={styles.adminTopbarSearchInput}
                                placeholder={searchPlaceholder}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onKeyDown={handleSearch}
                                onFocus={() => {
                                    if (isBillsPage && suggestions.length > 0) setShowSuggestions(true);
                                }}
                            />
                            {searchText && (
                                <button className={styles.adminTopbarSearchClear} onClick={handleClear}>
                                    <X size={14} />
                                </button>
                            )}

                            {/* Dropdown gợi ý */}
                            {isBillsPage && showSuggestions && (
                                <div className={styles.adminTopbarSuggest}>
                                    {suggestionsLoading ? (
                                        <div className={styles.adminTopbarSuggestLoading}>Đang tìm...</div>
                                    ) : suggestions.length === 0 ? (
                                        <div className={styles.adminTopbarSuggestEmpty}>Không tìm thấy kết quả</div>
                                    ) : (
                                        <>
                                            {suggestions.map((bill) => (
                                                <div
                                                    key={bill.id}
                                                    className={styles.adminTopbarSuggestItem}
                                                    onClick={() => handleSelectBill(bill.id)}
                                                >
                                                    <div className={styles.adminTopbarSuggestIcon}>
                                                        <FileText size={16} />
                                                    </div>
                                                    <div className={styles.adminTopbarSuggestInfo}>
                                                        <div className={styles.adminTopbarSuggestMain}>
                                                            <span className={styles.adminTopbarSuggestCode}>
                                                                {bill.code || `#${bill.id}`}
                                                            </span>
                                                            <span className={styles.adminTopbarSuggestAmount}>
                                                                {formatCurrency(bill.total)}
                                                            </span>
                                                        </div>
                                                        <div className={styles.adminTopbarSuggestSub}>
                                                            <User size={12} />
                                                            {bill.customer?.name || 'Khách lẻ'}
                                                            {bill.customer?.phone && (
                                                                <span> · {formatPhoneNumber(bill.customer.phone)}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <button
                                                className={styles.adminTopbarSuggestAll}
                                                onClick={handleSearchSubmit}
                                            >
                                                Xem tất cả kết quả cho "{searchText}"
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <button className={styles.adminTopbarSearchBtn} onClick={handleSearchSubmit}>
                            <Search />
                            Tìm
                        </button>
                    </div>
                )}
                {slot}
            </div>

            {location.pathname !== '/admin/bills' && location.pathname !== '/admin/customers' && (
                <div className={styles.adminTopbarRight}>
                    <button className={styles.adminTopbarIconBtn}>
                        <Bell />
                        <span className={styles.adminTopbarBadge} />
                    </button>

                    <div className={styles.adminTopbarDivider} />

                    <div
                        className={styles.adminTopbarUser}
                        onClick={() => hasPermission(PERMISSIONS.ADMIN.ALL) && navigate('/admin/settings/shop')}
                        style={{ cursor: hasPermission(PERMISSIONS.ADMIN.ALL) ? 'pointer' : 'default' }}
                    >
                        <div className={styles.adminTopbarUserInfo}>
                            <span className={styles.adminTopbarUserName}>{shopName || name}</span>
                            <span className={styles.adminTopbarUserRole}>{ROLE_LABELS[role] || role}</span>
                        </div>
                        <div className={styles.adminTopbarAvatar}>
                            <Store size={18} />
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
